import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import {
  Target,
  Layers,
  Cpu,
  Handshake,
  ShoppingCart,
  Receipt,
  Boxes,
  Settings,
  BarChart3,
  Plug,
} from 'lucide-react';
import TechnologiesGrid from '../../components/SystemInfo/TechnologiesGrid';

/**
 * 🧭 Página “Sobre el Sistema”
 */
const AboutSystem = () => {
  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      {/* 🧭 Contenedor principal */}
      <div className="flex flex-col gap-7.5">
        {/* 📘 Card principal */}
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          {/* 🔹 Encabezado */}
          <div className="w-full border-b border-stroke px-4 py-6 dark:border-strokedark sm:px-6 xl:px-9">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-black dark:text-white">
              <Cpu className="h-5 w-5 text-primary" />
              Sistema
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm leading-relaxed">
              Codenova POS es una plataforma moderna de gestión empresarial
              diseñada para optimizar las operaciones diarias de las empresas,
              desde la venta hasta el control de inventarios y la facturación
              electrónica, con un enfoque en eficiencia y cumplimiento fiscal.
            </p>
          </div>

          {/* 🔸 Contenido */}
          <div className="flex flex-col gap-10 p-4 sm:p-6 xl:p-9">
            {/* 🎯 Propósito */}
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Target className="h-6 w-6" />
              </div>

              <div>
                <h4 className="mb-2 text-base font-semibold text-black dark:text-white">
                  Propósito del Sistema
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  El objetivo principal de Codenova POS es ofrecer una
                  herramienta integral que permita a las empresas gestionar sus
                  procesos comerciales de manera eficiente, transparente y
                  alineada con las regulaciones fiscales vigentes.
                </p>
                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Desarrollado con tecnologías modernas y arquitectura web de
                  última generación, Codenova POS opera completamente en la
                  nube, garantizando disponibilidad, seguridad y acceso en
                  tiempo real desde cualquier lugar.
                </p>
              </div>
            </div>

            {/* 🧩 Módulos principales */}
            <div className="flex flex-col gap-5 sm:flex-row">
              {/* 📦 Ícono de la sección */}
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Layers className="h-6 w-6" />
              </div>

              {/* 📘 Contenido */}
              <div>
                <h4 className="mb-3 text-base font-semibold text-black dark:text-white">
                  Módulos Principales
                </h4>

                <ul className="text-gray-600 dark:text-gray-400 flex flex-col gap-3 text-sm leading-relaxed">
                  <li className="flex items-start gap-2">
                    <div className="mt-1 text-primary">
                      <ShoppingCart className="h-4 w-4" />
                    </div>
                    <p>
                      <strong>Ventas:</strong> Registro, control y emisión de
                      documentos fiscales electrónicos (DTE).
                    </p>
                  </li>

                  <li className="flex items-start gap-2">
                    <div className="mt-1 text-primary">
                      <Receipt className="h-4 w-4" />
                    </div>
                    <p>
                      <strong>Compras:</strong> Gestión de proveedores y control
                      de gastos.
                    </p>
                  </li>

                  <li className="flex items-start gap-2">
                    <div className="mt-1 text-primary">
                      <Boxes className="h-4 w-4" />
                    </div>
                    <p>
                      <strong>Inventario:</strong> Administración de existencias
                      y movimientos.
                    </p>
                  </li>

                  <li className="flex items-start gap-2">
                    <div className="mt-1 text-primary">
                      <Settings className="h-4 w-4" />
                    </div>
                    <p>
                      <strong>Configuración:</strong> Datos empresariales,
                      usuarios, roles y permisos.
                    </p>
                  </li>

                  <li className="flex items-start gap-2">
                    <div className="mt-1 text-primary">
                      <BarChart3 className="h-4 w-4" />
                    </div>
                    <p>
                      <strong>Reportes:</strong> Estadísticas detalladas y
                      exportación en PDF o Excel.
                    </p>
                  </li>

                  <li className="flex items-start gap-2">
                    <div className="mt-1 text-primary">
                      <Plug className="h-4 w-4" />
                    </div>
                    <p>
                      <strong>Integraciones:</strong> Conexión con Hacienda,
                      impresoras y servicios externos.
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            {/* ⚙️ Tecnología y arquitectura */}
            <div className="flex flex-col gap-5 sm:flex-row">
              {/* 🔹 Ícono principal */}
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Cpu className="h-6 w-6" />
              </div>

              {/* 🔹 Contenido textual + íconos */}
              <div className="flex-1">
                <h4 className="mb-2 text-base font-semibold text-black dark:text-white">
                  Tecnología y Arquitectura
                </h4>

                {/* Descripción */}
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  Codenova POS está construido sobre una arquitectura moderna
                  basada en <strong>Java 21 (Spring Boot)</strong> para el
                  backend y <strong>React + TypeScript + Redux</strong> para el
                  frontend, ofreciendo una experiencia fluida, modular y
                  altamente escalable.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Para la comunicación cliente-servidor, se utiliza{' '}
                  <strong>Axios</strong> junto con una capa centralizada de
                  servicios API, garantizando eficiencia, trazabilidad y
                  consistencia en las solicitudes HTTP.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  El backend se despliega como{' '}
                  <strong>contenedores Docker</strong>, asegurando entornos
                  aislados, reproducibles y portables, acelerando la entrega
                  continua y facilitando la escalabilidad horizontal.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  La infraestructura corre sobre{' '}
                  <strong>AWS ECS (Elastic Container Service)</strong>{' '}
                  utilizando <strong>Fargate</strong> para ejecución serverless
                  de contenedores, eliminando la necesidad de administrar
                  servidores y permitiendo un escalado automático según demanda
                  real.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  El tráfico HTTP es distribuido por un{' '}
                  <strong>Application Load Balancer (ALB)</strong>, garantizando
                  disponibilidad, tiempos de respuesta consistentes y failover
                  inteligente entre tareas.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Los recursos estáticos del frontend se sirven mediante{' '}
                  <strong>Amazon S3</strong> y se distribuyen globalmente con{' '}
                  <strong>Amazon CloudFront</strong>, proporcionando tiempos de
                  carga rápidos, caché global y protección adicional contra
                  ataques.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Las conexiones externas y enrutamiento público se administran
                  con <strong>Amazon Route 53</strong>, permitiendo balanceo
                  inteligente, redundancia DNS y certificados SSL gestionados.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Los artefactos se almacenan de forma privada en{' '}
                  <strong>Amazon Elastic Container Registry (ECR)</strong>,
                  asegurando despliegues confiables, versionados y seguridad de
                  imágenes.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  El almacenamiento persistente se gestiona mediante{' '}
                  <strong>Amazon EFS (Elastic File System)</strong>,
                  compartiendo datos entre contenedores independientemente de la
                  disponibilidad de tareas, ideal para archivos dinámicos del
                  sistema (impresiones, reportes, certificados, etc.).
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  La base de datos principal utiliza{' '}
                  <strong>Amazon RDS (PostgreSQL)</strong> con backups
                  automáticos, réplica multi-AZ y monitoreo continuo para
                  asegurar seguridad e integridad transaccional.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  El despliegue continuo se administra con{' '}
                  <strong>GitHub Actions (CI/CD)</strong>, automatizando
                  construcciones, pruebas, seguridad de dependencias y
                  despliegues directos hacia ECS mediante pipelines confiables.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  El sistema es totalmente <strong>multi-tenant</strong>,
                  permitiendo operar múltiples empresas de forma aislada en
                  datos, almacenamiento y recursos, manteniendo flexibilidad y
                  cumplimiento de seguridad.
                </p>

                {/* Grilla de íconos */}
                <TechnologiesGrid />
              </div>
            </div>

            {/* 🤝 Compromiso */}
            <div className="flex flex-col gap-5 sm:flex-row">
              {/* Ícono principal */}
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Handshake className="h-6 w-6" />
              </div>

              {/* Contenido */}
              <div>
                <h4 className="mb-2 text-base font-semibold text-black dark:text-white">
                  Compromiso
                </h4>

                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  En <strong>Codenova POS</strong> trabajamos con el firme
                  compromiso de ofrecer una plataforma confiable, eficiente y en
                  constante evolución, diseñada para acompañar el crecimiento de
                  cada empresa que confía en nosotros.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Nos enfocamos en brindar soluciones tecnológicas que
                  simplifiquen las operaciones diarias, optimicen la gestión y
                  fortalezcan la toma de decisiones empresariales, garantizando
                  siempre la seguridad, estabilidad y cumplimiento normativo.
                </p>

                <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm leading-relaxed">
                  Nuestro compromiso no termina con la implementación:
                  mantenemos una relación de acompañamiento continuo,
                  incorporando mejoras constantes, actualizaciones fiscales y
                  soporte técnico personalizado que asegura el éxito a largo
                  plazo de nuestros clientes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AboutSystem;
